'use client';

import { useTracker } from '@lichtblick/tracker';
import { useSearchParams } from 'next/navigation';
import { type FC, useCallback, useLayoutEffect } from 'react';
import type { RootState } from '@/app/checkout/(with-tariff-data)/schema';
import { CardWrapper } from '@/components/electricity-tariffs/card';
import { SelectCta } from '@/components/electricity-tariffs/select-cta';
import { useElectricityTariffs } from '@/components/tariffs-providers/use-electricity-tariffs';
import { NO_TARIFFS_ERROR_CAUSE } from '@/constants';
import type { CheckoutType } from '@/helpers/set-state-cookie';
import { createEcommerceItem } from '@/tracking/create-ecommerce-item';
import { useLoggedIn } from '@/tracking/use-logged-in';
import { ElectricityCard } from './electricity-card';

type ElectricityCardsProps = {
  isComboCheckout: boolean;
  params: RootState;
  checkout: CheckoutType;
  submitAction: (state: undefined, formData: FormData) => Promise<undefined>;
};

export const ElectricityCards: FC<ElectricityCardsProps> = ({ isComboCheckout, params, checkout, submitAction }) => {
  const loggedIn = useLoggedIn();
  const searchParams = useSearchParams();
  const { trackEcommerceEvent } = useTracker();
  const { tariffs } = useElectricityTariffs() || {};

  const displayedTariffs = tariffs?.filter((tariff) =>
    checkout === 'vario' ? tariff.productCode?.includes('VARIO') : !tariff.productCode?.includes('VARIO'),
  );

  const selectedProductCode = searchParams.get('productCode');
  const cardIndex = displayedTariffs?.findIndex((tariff) => tariff.productCode === selectedProductCode);
  const selectedProduct = displayedTariffs?.find((tariff) => tariff.productCode === selectedProductCode);

  const trackAddToCart = useCallback(() => {
    if (!selectedProduct || cardIndex === undefined) {
      return;
    }

    trackEcommerceEvent({
      event: 'Ecommerce - Add to cart',
      event_name: 'add_to_cart',
      add_to_cart: createEcommerceItem([{ ...selectedProduct, itemIndex: cardIndex }], loggedIn, isComboCheckout),
    });
  }, [cardIndex, isComboCheckout, loggedIn, selectedProduct, trackEcommerceEvent]);

  useLayoutEffect(() => {
    if (selectedProductCode) {
      return;
    }

    const preselectedTariff = displayedTariffs?.find((tariff) =>
      tariff.productCode?.includes(checkout === 'vario' ? 'VARIO' : 'RELAX'),
    );

    if (preselectedTariff?.productCode) {
      const params = new URLSearchParams(searchParams.toString());
      params.set('productCode', preselectedTariff.productCode);

      history.replaceState(null, '', `?${params.toString()}`);
    }
  }, [selectedProductCode, displayedTariffs, searchParams, checkout]);

  if (!displayedTariffs?.length) {
    throw new Error('No tariffs available', { cause: NO_TARIFFS_ERROR_CAUSE });
  }

  return (
    <>
      <CardWrapper aria-label="Verfügbare Tarife">
        {displayedTariffs.map((product, index) => (
          <ElectricityCard
            cardIndex={index}
            isComboCheckout={isComboCheckout}
            key={`${index}_${product.productCode}`}
            product={product}
            isSelected={selectedProduct?.productCode === product.productCode}
          />
        ))}
      </CardWrapper>

      <SelectCta
        loggedIn={loggedIn}
        product={selectedProduct}
        params={params}
        submitAction={submitAction}
        trackAddToCart={trackAddToCart}
      />
    </>
  );
};
